import { BaseGame, GameApp } from "@casino/game-framework";
import { GameBoard } from "./GameBoard";
import { GameRules } from "./GameRules";
import { DragDropManager } from "./DragDropManager";
import { UIManager } from "./UIManager";
import { MenuManager } from "./MenuManager";

export class SolitaireGame extends BaseGame {
  private board: GameBoard;
  private rules: GameRules;
  private dragDropManager: DragDropManager;
  private ui: UIManager;
  private menuManager: MenuManager | null = null;

  constructor(app: GameApp) {
    super(app);

    this.rules = new GameRules();
    this.board = new GameBoard({
      width: this.app.app.screen.width,
      height: this.app.app.screen.height,
    });
    this.ui = new UIManager({
      screenWidth: this.app.app.screen.width,
      screenHeight: this.app.app.screen.height,
    });
    this.dragDropManager = new DragDropManager(
      this.app.stage,
      this.board,
      this.rules
    );

    // Update background size when renderer resizes
    this.setupBackgroundResize();
  }

  private setupBackgroundResize(): void {
    // Update background and layout when window resizes or stage transforms
    const updateLayout = () => {
      const viewportWidth = window.innerWidth || 1920;
      const viewportHeight = window.innerHeight || 1080;
      const designWidth = 1280;
      const designHeight = 720;
      
      // Get current stage transform
      const stage = this.app.stage;
      const stageScale = stage.scale.x; // Stage uses uniform scale
      const stageX = stage.x;
      const stageY = stage.y;
      
      // Calculate canvas size in stage coordinates
      // When stage is scaled and centered, we need to cover the entire canvas area
      const canvasWidthInStage = viewportWidth / stageScale;
      const canvasHeightInStage = viewportHeight / stageScale;
      
      // Account for stage position offset (when centered, stage.x and stage.y are non-zero)
      // The background needs to extend to cover areas outside the stage bounds
      // Reduced padding from 2000 to 500 for more reasonable background size
      const padding = 500;
      const width = Math.ceil(canvasWidthInStage) + padding * 2;
      const height = Math.ceil(canvasHeightInStage) + padding * 2;
      
      this.board.updateBackgroundSize(width, height);
      
      // Position background to cover entire canvas area
      // Account for stage position offset in stage coordinates
      const feltPattern = this.board.children.find(child => child.name === "FeltPattern");
      if (feltPattern) {
        // Position to cover the entire canvas area, accounting for stage offset
        feltPattern.x = (-stageX / stageScale) - padding;
        feltPattern.y = (-stageY / stageScale) - padding;
      }
      
      // Also update matrix background position if it exists
      const matrixBackground = this.board.children.find(child => child.name === "MatrixBackground");
      if (matrixBackground) {
        // Position to cover the entire canvas area, accounting for stage offset
        // Use same calculation as felt pattern for consistency
        // The background is sized to cover canvas + padding on all sides
        // Position it so it covers from (-stageX/scale - padding) to cover the entire visible area
        const matrixX = (-stageX / stageScale) - padding;
        const matrixY = (-stageY / stageScale) - padding;
        matrixBackground.x = matrixX;
        matrixBackground.y = matrixY;
        
        // Debug: Log positioning to help diagnose issues
        // console.log('MatrixBackground positioned:', { matrixX, matrixY, stageX, stageY, stageScale, width, height });
      }
      
      // Update game layout for responsive design
      // Use stage dimensions (design resolution) for layout calculations
      // The stage maintains design resolution coordinates regardless of scaling
      this.board.updateLayout(designWidth, designHeight);
    };
    
    // Initial update - call immediately and also with delay to ensure renderer is ready
    // This ensures background is positioned correctly from the start
    updateLayout();
    setTimeout(updateLayout, 100);
    
    // Update on resize
    window.addEventListener('resize', updateLayout);
    window.addEventListener('orientationchange', () => {
      setTimeout(updateLayout, 200); // Extra delay for orientation change
    });
    
    // Also update when stage transforms (scale/position changes)
    // Use a ticker to check for stage changes
    let lastStageX = this.app.stage.x;
    let lastStageY = this.app.stage.y;
    let lastStageScale = this.app.stage.scale.x;
    
    this.app.ticker.add(() => {
      const currentStageX = this.app.stage.x;
      const currentStageY = this.app.stage.y;
      const currentStageScale = this.app.stage.scale.x;
      
      if (currentStageX !== lastStageX || currentStageY !== lastStageY || currentStageScale !== lastStageScale) {
        lastStageX = currentStageX;
        lastStageY = currentStageY;
        lastStageScale = currentStageScale;
        updateLayout();
      }
    });
  }

  init(): void {
    // Add board first (background layer)
    this.app.stage.addChild(this.board);
    
    // Center the game board on the stage
    this.centerGameBoard();
    
    // Add UI last to ensure it's on top
    this.app.stage.addChild(this.ui);
    
    // Ensure UI is always on top (above drag container and board, but below FPS meter)
    this.ensureUIOnTop();

    // Apply initial layout centering
    this.applyLayout();

    this.setupEventHandlers();
    this.startNewGame();
  }

  private centerGameBoard(): void {
    // Center the game board container on the stage
    // Stage uses design resolution (1280x720)
    const designWidth = 1280;
    const designHeight = 720;
    
    // Calculate bounds of all game content
    // For now, assume content spans the design resolution
    // The board content is already positioned relative to (0,0)
    // So we don't need to offset the board itself
    this.board.x = 0;
    this.board.y = 0;
  }

  private applyLayout(): void {
    // Use design resolution for layout calculations
    const designWidth = 1280;
    const designHeight = 720;
    this.board.updateLayout(designWidth, designHeight);
  }

  private ensureUIOnTop(): void {
    // Bring UI to the very front (but below FPS meter if it exists)
    const uiIndex = this.app.stage.children.indexOf(this.ui);
    if (uiIndex !== -1) {
      // If FPS meter exists, place UI just below it, otherwise at the top
      if (this.fpsMeter) {
        const fpsIndex = this.app.stage.children.indexOf(this.fpsMeter);
        if (fpsIndex !== -1) {
          this.app.stage.setChildIndex(this.ui, fpsIndex);
        }
      } else {
        this.app.stage.setChildIndex(this.ui, this.app.stage.children.length - 1);
      }
    }
  }

  private setupEventHandlers(): void {
    this.ui.setNewGameHandler(() => this.startNewGame());
    
    // Connect menu manager's new game button to handler
    if (this.menuManager) {
      this.menuManager.setNewGameHandler(() => this.startNewGame());
    }
    
    this.board.stock.setPlaceholderInteractive(() => this.handleStockClick());
    this.dragDropManager.setOnMoveComplete(() => this.onMoveComplete());
    this.dragDropManager.setOnStockClick(() => this.handleStockClick());
  }

  /**
   * Set the menu manager instance
   */
  setMenuManager(menuManager: MenuManager): void {
    this.menuManager = menuManager;
    // Connect new game handler
    this.menuManager.setNewGameHandler(() => this.startNewGame());
  }

  private startNewGame(): void {
    // Clear any cards stuck in drag container
    this.dragDropManager.clearDragContainer();
    this.board.dealNewGame();
    this.ui.resetMoves();
    this.ui.resetTimer();
    this.ui.startTimer();
    this.ui.hideWinMessage();
    this.setupAllCardInteractions();
  }

  private setupAllCardInteractions(): void {
    const allPiles = this.board.getAllPiles();
    allPiles.forEach(pile => {
      pile.cards.forEach(card => {
        this.dragDropManager.setupCardInteraction(card);
      });
      
      // For stock pile, ensure top card is accessible
      if (pile.type === "stock" && !pile.isEmpty()) {
        const topCard = pile.getTopCard();
        if (topCard) {
          // Bring top card to front
          pile.removeChild(topCard);
          pile.addChild(topCard);
          this.dragDropManager.setupCardInteraction(topCard);
        }
      }
    });
  }

  private handleStockClick(): void {
    const flipped = this.board.flipStockToWaste();
    if (flipped) {
      // Set up interaction for the newly flipped waste card
      const topCard = this.board.waste.getTopCard();
      if (topCard) {
        this.dragDropManager.setupCardInteraction(topCard);
      }
    } else {
      // Stock was empty, cards were cycled back from waste
      // Set up interactions for all stock cards
      this.board.stock.cards.forEach(card => {
        this.dragDropManager.setupCardInteraction(card);
      });
    }
    this.ui.incrementMoves();
  }

  private onMoveComplete(): void {
    this.ui.incrementMoves();
    this.checkWinCondition();
  }

  private checkWinCondition(): void {
    if (this.rules.isGameWon(this.board.foundation)) {
      this.ui.stopTimer();
      this.ui.showWinMessage();
    }
  }

  update(delta: number): void {
    // Update Matrix background animation if active
    this.board.updateMatrixBackground();
  }

  /**
   * Change game board background color scheme
   */
  changeBackground(colorScheme: string): void {
    this.board.changeBackground(colorScheme);
    // Trigger background positioning update after background change
    // Use setTimeout to ensure the new background is created before positioning
    setTimeout(() => {
      this.updateBackgroundPosition();
    }, 0);
  }

  /**
   * Update background positioning (called after background changes)
   */
  private updateBackgroundPosition(): void {
    const viewportWidth = window.innerWidth || 1920;
    const viewportHeight = window.innerHeight || 1080;
    const designWidth = 1280;
    const designHeight = 720;
    
    // Get current stage transform
    const stage = this.app.stage;
    const stageScale = stage.scale.x;
    const stageX = stage.x;
    const stageY = stage.y;
    
    const padding = 500;
    
    // Position matrix background if it exists
    const matrixBackground = this.board.children.find(child => child.name === "MatrixBackground");
    if (matrixBackground) {
      const matrixX = (-stageX / stageScale) - padding;
      const matrixY = (-stageY / stageScale) - padding;
      matrixBackground.x = matrixX;
      matrixBackground.y = matrixY;
    }
    
    // Position felt pattern if it exists
    const feltPattern = this.board.children.find(child => child.name === "FeltPattern");
    if (feltPattern) {
      feltPattern.x = (-stageX / stageScale) - padding;
      feltPattern.y = (-stageY / stageScale) - padding;
    }
  }
}
